import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy - The Home Library Project</title>
        <meta name="description" content="Privacy Policy for The Home Library Project" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            How we protect and handle your information
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-sm text-[#4a4a4a] mb-2"><strong>The Home Library Project</strong></p>
            <p className="text-sm text-[#4a4a4a] mb-2"><strong>Effective Date:</strong> November 24, 2025</p>
            <p className="text-sm text-[#4a4a4a] mb-8"><strong>Last Updated:</strong> November 24, 2025</p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">1. Introduction</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              The Home Library Project ("we," "our," or "us") is a Delaware nonprofit organization committed to promoting childhood literacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, make a donation, purchase products, or otherwise interact with our services.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">2.1 Personal Information You Provide</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
              <li><strong>Payment Information:</strong> Credit card details, billing address (processed securely through Stripe)</li>
              <li><strong>Donation Information:</strong> Donation amount, frequency, and designation</li>
              <li><strong>Account Information:</strong> Username, password, and preferences if you create an account</li>
              <li><strong>Communications:</strong> Information you provide when contacting us or signing up for newsletters</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              When you access our services, we may automatically collect:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked</li>
              <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">2.3 Information from Third Parties</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We may receive information from:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>Payment Processors:</strong> Transaction details from Stripe</li>
              <li><strong>Social Media Platforms:</strong> If you interact with us through social media</li>
              <li><strong>Public Databases:</strong> Information to verify addresses or contact details</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We use your information for the following purposes:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>Processing Transactions:</strong> To process donations, purchases, and fulfill orders</li>
              <li><strong>Communication:</strong> To send receipts, updates, newsletters, and respond to inquiries</li>
              <li><strong>Tax Documentation:</strong> To provide donation receipts and comply with IRS requirements</li>
              <li><strong>Service Improvement:</strong> To analyze usage and improve our website and services</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Fraud Prevention:</strong> To detect and prevent fraudulent transactions</li>
              <li><strong>Marketing:</strong> To send information about our programs and initiatives (with your consent)</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">4. Payment Processing</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We use Stripe as our third-party payment processor. When you make a donation or purchase:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Your payment information is transmitted directly to Stripe using secure encryption</li>
              <li>We do not store complete credit card numbers on our servers</li>
              <li>Stripe's processing is subject to their Privacy Policy: https://stripe.com/privacy</li>
              <li>Payment data is encrypted and stored securely in compliance with PCI-DSS standards</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">5. How We Share Your Information</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We do not sell, trade, or rent your personal information. We may share information in the following circumstances:
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.1 Service Providers</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We share information with trusted third-party service providers who assist us with:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Payment processing (Stripe)</li>
              <li>Email delivery services</li>
              <li>Website hosting and analytics</li>
              <li>Customer relationship management</li>
              <li>Shipping and fulfillment</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              These providers are contractually obligated to protect your information and use it only for specified purposes.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.2 Legal Requirements</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We may disclose information when required by law, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>In response to subpoenas, court orders, or legal processes</li>
              <li>To protect our rights, property, or safety</li>
              <li>To prevent fraud or illegal activity</li>
              <li>To comply with tax and regulatory requirements</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.3 Business Transfers</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.4 With Your Consent</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We may share information for other purposes with your explicit consent.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">6. Data Security</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure servers and databases</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>PCI-DSS compliance for payment processing</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">7. Data Retention</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We retain your information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Fulfill the purposes outlined in this Privacy Policy</li>
              <li>Comply with legal and regulatory requirements (including IRS record-keeping requirements)</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Donation records are retained for at least seven years for tax compliance purposes.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">8. Your Rights and Choices</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Depending on your location, you may have the following rights:
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.1 Access and Correction</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You may request access to or correction of your personal information by contacting us.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.2 Deletion</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You may request deletion of your personal information, subject to legal retention requirements.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.3 Marketing Communications</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              You may opt out of marketing emails by:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Clicking the "unsubscribe" link in our emails</li>
              <li>Contacting us directly</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You will continue to receive transactional emails related to your donations or purchases.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.4 Cookies</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.5 Do Not Track</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our website does not currently respond to "Do Not Track" signals.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If we learn we have collected such information, we will promptly delete it.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">10. Third-Party Links</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">11. California Privacy Rights</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>Right to Know:</strong> What personal information we collect, use, and share</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal information)</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              To exercise these rights, contact us using the information below.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">12. International Users</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our services are based in the United States. If you access our services from outside the U.S., your information may be transferred to and processed in the United States, which may have different data protection laws than your country.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We may update this Privacy Policy periodically. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Posting the updated policy on our website</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification (for significant changes)</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">14. Contact Information</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-2">
              If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:
            </p>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-2">
              <strong>The Home Library Project</strong><br/>
              Email: info@thehomelibraryproject.org<br/>
              Address: 224 Hawkes Court, Hockessin, DE 19707
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">15. Donor Privacy Commitment</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              As a nonprofit organization, we are committed to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Protecting donor information and using it only for legitimate organizational purposes</li>
              <li>Not selling, trading, or sharing donor information with third parties for their marketing purposes</li>
              <li>Honoring donor requests regarding privacy preferences</li>
              <li>Providing transparency about how donations are used</li>
            </ul>

            <hr className="my-8 border-gray-300" />

            <p className="text-sm text-[#4a4a4a] leading-relaxed italic">
              <strong>Acknowledgment:</strong> By using our services, making a donation, or providing your information, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
